import {
  buildCustomFieldChangeset,
  type CustomFieldChangeset,
  type TargetEntityType,
} from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";

import { CustomFieldValuesTable } from "./CustomFieldValuesTable";

export interface EntityCustomFieldsProps {
  /** Entity type whose Ready fields are shown. */
  entityType: TargetEntityType;

  /** Org that owns the entity. */
  orgId: string;

  /** Current values by field name (an entity record's `custom_fields`). */
  values: Record<string, unknown>;

  /** Apply one set-or-clear changeset via the host's update mutation. */
  commit: (changeset: CustomFieldChangeset) => Promise<unknown>;

  /** Lift a commit failure to the host's page-level alert. */
  onError: (error: Error | null) => void;

  /**
   * How the section frames itself, forwarded to the value table: `"card"` for a
   * self-contained bordered surface, `"plain"` for a borderless section flush
   * within a list.
   */
  variant: "card" | "plain";
}

/**
 * Custom Fields section for an entity detail surface. Reads the entity type's
 * Ready field definitions and renders the shared value table over `values`,
 * turning each inline set or clear into a changeset handed to `commit`.
 *
 * Entity-agnostic: the host supplies `values` and a `commit` closure adapting
 * its own update mutation. Gated behind the `release-custom-fields` flag.
 */
export function EntityCustomFields({
  entityType,
  orgId,
  values,
  commit,
  onError,
  variant,
}: EntityCustomFieldsProps) {
  const flagEnabled = useCheckFeatureFlag("release-custom-fields");

  // Fetch the Ready fields in parallel, regardless of the feature flag.
  // This speeds up retrieval, and the cost is a potentially unnecessary network call.
  const readyQuery = useReadyCustomFields({ entityType, orgId });

  async function runCommit(changeset: CustomFieldChangeset) {
    // Clear any stale alert before committing; a fresh failure re-surfaces one.
    onError(null);
    try {
      await commit(changeset);
    } catch (error) {
      onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  function handleSet(fieldName: string, value: unknown) {
    void runCommit(
      buildCustomFieldChangeset({ setFields: { [fieldName]: value } }),
    );
  }

  function handleClear(fieldName: string) {
    void runCommit(buildCustomFieldChangeset({ clearFields: [fieldName] }));
  }

  if (!flagEnabled) {
    return null;
  }

  return (
    <CustomFieldValuesTable
      entityType={entityType}
      readyFields={readyQuery.data ?? []}
      isLoading={readyQuery.isLoading}
      values={values}
      onSet={handleSet}
      onClear={handleClear}
      variant={variant}
    />
  );
}
